import { useEffect, useRef, useState } from "react";
import { CloseCircleOutlined, PartitionOutlined } from "@ant-design/icons";
import AppPaths from "constants/appPaths";
import { fetchDataSetFieldInsights, fetchDataSetsBySchemaId, type SiblingDataSet } from "./dataSetApi";
import { buildSiblingSummaries, selectProfilingTargets, summariseInsights, type SiblingProfilingSummary, type SiblingSummary } from "./compareSchemaDataSets";
import { buildToolError } from "./errorEnvelope";
import type { ToolPartProps } from "./types";

interface CompareSchemaDataSetsInput {
    includeProfiling?: boolean;
    description: string;
}

interface CompareSchemaDataSetsOutput {
    schemaId?: string;
    schemaName?: string;
    currentDataSetId?: string;
    dataSetCount: number;
    siblings: SiblingSummary[];
    /** Deep link into the Schema view whose dataset list holds these data sets. */
    schemaLink?: string;
    /** True when the schema has more data sets than one page returned. */
    morePagesAvailable?: boolean;
    /** Set when profiling was requested but only fetched for a bounded subset. */
    profilingLimitedTo?: number;
    note?: string;
    description: string;
}

function buildSchemaLink(tenant?: string, businessarea?: string, schemaId?: string): string | undefined {
    if (!tenant || !businessarea || !schemaId) return undefined;
    return AppPaths.TENANT_BUSINESS_AREA_SCHEMA_ID_ACTION.replace(":tenant", tenant.toLowerCase())
        .replace(":businessarea", businessarea.toLowerCase())
        .replace(":schemaId", schemaId)
        .replace(":schemaAction", "view");
}

export default function CompareSchemaDataSetsTool({
    toolCallId,
    state,
    input,
    output,
    errorText,
    helpers,
}: ToolPartProps<CompareSchemaDataSetsInput, CompareSchemaDataSetsOutput>) {
    const { addToolOutput, tenantId, businessAreaId, schemaId, dataSet, urlParams, history } = helpers;
    const resolvedRef = useRef(false);
    const [pending, setPending] = useState(false);

    useEffect(() => {
        if (resolvedRef.current) return;
        if (state !== "input-available") return;
        resolvedRef.current = true;

        const description = input?.description || "";
        const schemaLink = buildSchemaLink(urlParams?.tenant, urlParams?.businessarea, schemaId);

        if (!tenantId || !businessAreaId || !schemaId) {
            addToolOutput({
                tool: "compareSchemaDataSets",
                toolCallId,
                output: buildToolError(
                    { message: "Missing tenant, business area, or schema context to list the sibling data sets." },
                    "Could not compare against the other data sets because the schema context is incomplete.",
                ),
            });
            return;
        }

        setPending(true);
        (async () => {
            try {
                const page = await fetchDataSetsBySchemaId(tenantId, businessAreaId, schemaId);
                const all: SiblingDataSet[] = page.items || [];
                const rows = buildSiblingSummaries(all, dataSet?.dataSetId);

                let profilingLimitedTo: number | undefined;
                if (input?.includeProfiling) {
                    const targets = selectProfilingTargets(rows);
                    if (rows.length > targets.length) profilingLimitedTo = targets.length;
                    const results = await Promise.all(
                        targets.map(async (t) => {
                            try {
                                const insights = await fetchDataSetFieldInsights(tenantId, t.dataSetId);
                                return { dataSetId: t.dataSetId, profiling: summariseInsights(insights) };
                            } catch {
                                return { dataSetId: t.dataSetId, profiling: undefined as SiblingProfilingSummary | undefined };
                            }
                        }),
                    );
                    const byId = new Map(results.map((r) => [r.dataSetId, r.profiling]));
                    for (const row of rows) {
                        const p = byId.get(row.dataSetId);
                        if (p) row.profiling = p;
                    }
                }

                const current = all.find((ds) => ds.dataSetId === dataSet?.dataSetId);
                addToolOutput({
                    tool: "compareSchemaDataSets",
                    toolCallId,
                    output: {
                        schemaId,
                        schemaName: (current?.schemaName as string) || (all[0]?.schemaName as string) || undefined,
                        currentDataSetId: dataSet?.dataSetId,
                        dataSetCount: rows.length,
                        siblings: rows,
                        schemaLink,
                        morePagesAvailable: !!page.pageKey,
                        profilingLimitedTo,
                        note:
                            rows.length <= 1
                                ? "This is the only data set under this schema, so there is nothing to compare it against."
                                : undefined,
                        description,
                    } as CompareSchemaDataSetsOutput,
                });
            } catch (e: any) {
                addToolOutput({
                    tool: "compareSchemaDataSets",
                    toolCallId,
                    output: {
                        ...buildToolError(
                            e?.response?.data || e,
                            "Could not load the other data sets under this schema. You can open the Schema view to compare them directly.",
                        ),
                        schemaLink,
                    },
                });
            } finally {
                setPending(false);
            }
        })();
    }, [state, input, tenantId, businessAreaId, schemaId, dataSet, urlParams, addToolOutput, toolCallId]);

    const description = (state === "output-available" && output?.description) || input?.description || "";
    const rows = state === "output-available" && output ? output.siblings.slice(0, 6) : [];
    const schemaLink = (state === "output-available" && output?.schemaLink) || undefined;

    return (
        <div className="rounded-2xl border border-zinc-200 bg-white p-3 text-sm text-zinc-800 shadow-sm">
            <div className="mb-1 flex items-center gap-2 text-[#001D3D]">
                <PartitionOutlined />
                <span className="text-xs font-semibold uppercase tracking-wider">Schema comparison</span>
            </div>
            {description && <div className="text-xs text-zinc-600">{description}</div>}
            {pending && <div className="mt-1 text-xs text-zinc-500">Loading the other data sets under this schema...</div>}
            {state === "output-available" && output && (
                <div className="mt-1 text-xs text-zinc-500">
                    {output.dataSetCount === 0
                        ? "No data sets found under this schema."
                        : `${output.dataSetCount} data set${output.dataSetCount === 1 ? "" : "s"} under this schema.`}
                </div>
            )}
            {rows.length > 0 && (
                <ul className="mt-2 flex flex-col gap-1">
                    {rows.map((r) => (
                        <li
                            key={r.dataSetId}
                            className={`rounded-md border px-2 py-1 text-[11px] ${r.isCurrent ? "border-blue-300 bg-blue-50 text-blue-800" : "border-zinc-200 bg-zinc-50 text-zinc-700"}`}
                        >
                            <span className="font-medium">{r.dataSetName}</span>
                            {r.isCurrent ? <span className="text-[10px] text-blue-500">{" (current)"}</span> : null}
                            <span className="text-[10px] text-zinc-500">
                                {" · "}
                                {r.recordCount ?? "?"} records
                                {r.erroredRecordsCount !== undefined ? ` · ${r.erroredRecordsCount} errored` : ""}
                                {r.errorRatePct !== undefined ? ` (${r.errorRatePct}%)` : ""}
                                {r.rating !== undefined ? ` · rating ${r.rating}/5` : ""}
                            </span>
                        </li>
                    ))}
                    {output && output.dataSetCount > rows.length && (
                        <li className="text-[10px] text-zinc-400">...and {output.dataSetCount - rows.length} more.</li>
                    )}
                </ul>
            )}
            {schemaLink && (
                <button type="button" className="mt-2 text-[11px] text-blue-600 underline" onClick={() => history.push(schemaLink)}>
                    Open the Schema view dataset list
                </button>
            )}
            {state === "output-error" && (
                <div className="mt-2 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> {errorText || "Tool error."}
                </div>
            )}
        </div>
    );
}
